<template>
  <div class="lcu-upload-wrapper" :class="[{
    'lcu-cropper-open': openCropper,
    'lcu-upload-right': align === 'right'
  }, wrapperClasses]">
    <div class="lcu-upload-cropper" v-show="openCropper">
      <img
        v-if="openCropper"
        ref="imageCropper"
        :src="openCropper"
        style="display:block;max-width:100%;" />
    </div>
    <div class="lcu-buttons-container">
      <button
        class="lcu-button-cancel"
        @click.prevent="cancelUpload"
        v-if="openCropper && !activeUpload">
        Cancelar
      </button>
      <button
        v-if="!openCropper && !filePicked"
        type="button"
        class="lcu-button-upload"
        @click.prevent="openUpload"
        :name="name">
        <span
          class="lcu-button-chooseText">
          {{ text }}
        </span>
      </button>
      <button
        v-if="openCropper || filePicked"
        type="button"
        class="lcu-button-upload"
        :class="{
          'lcu-button-uploading': activeUpload,
        }"
        :style="{ 'max-width': maxWidth }"
        @click.prevent="uploadFile"
        :disabled="activeUpload"
        :name="name">
        <span
          class="lcu-button-uploadText"
          :class="{ 'lcu-upload-hide': activeUpload }">
          {{ uploadText || 'Upload' }} <span v-if="allTypes">{{ fileName }}</span>
        </span>
        <div
          v-if="activeUpload"
          :style="{
            width: activeUpload.progress
          }"
          class="lcu-upload-progress">
          <span class="lcu-upload-perc">{{ activeUpload.progress }}</span>
        </div>
      </button>
      <input
        ref="uploadFile"
        type="file"
        name="profilePic"
        :accept="allTypes ? '*' : 'image/*'"
        style="width: 140px;"
        class="lcu-upload"
        @change.prevent="openLocalImg($event)" />
    </div>
  </div>
</template>

<script>
import mime from 'mime';
import Cropper from 'cropperjs';
import Auth from '@/mixins/Auth.vue';
import { NOTIFICATION_SUCCESS, NOTIFICATION_ERROR } from '@/globals/constants';

export default {
  name: 'button-upload',
  mixins: [Auth],
  props: {
    name: {
      type: String,
      required: false,
    },
    text: {
      type: String,
      required: false,
      default: 'Button Text',
    },
    crop: {
      type: Boolean,
      required: false,
      default: false,
    },
    align: {
      type: String,
      required: false,
      default: 'left',
    },
    bucketPath: {
      type: String,
      required: false,
      default: '',
    },
    uploadText: {
      type: String,
      required: false,
    },
    maxWidth: {
      type: [Boolean, String],
      required: false,
      default: false,
    },
    wrapperClasses: {
      type: [Boolean, Array],
      required: false,
      default: false,
    },
    image: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    allTypes: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      progress: 1,
      openCropper: false,
      cropper: null,
      filePicked: false,
      fileName: '',
      activeUpload: null,
      rawFile: null,
    };
  },
  watch: {
    openCropper(file) {
      if (file && this.crop) {
        setTimeout(() => {
          const image = this.$refs.imageCropper;
          this.cropper = new Cropper(image, {
            aspectRatio: this.image.ratio || 1 / 1,
            dragMode: 'move',
            autoCropArea: 1,
            zoomable: false,
          });
        }, 300);
      }
      this.$emit('on-cropper-toggle', file);
    },
  },
  beforeUnmount() {
    this.destroyCropper();
  },
  methods: {
    onClick() {
      this.$emit('on-click');
      this.filePicked = false;
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    async openLocalImg(e) {
      const evt = e;
      let newFile = [...evt.target.files];
      if (newFile.length > 0) {
        try {
          this.filePicked = true;
          [newFile] = newFile;
          const base64File = await this.fileToBase64(newFile);
          if (!this.allTypes) {
            const img = new Image();
            img.src = base64File;
            await new Promise((r) => { img.onload = () => r(); });
            this.openCropper = base64File;
          }
          this.fileName = this.getFile(newFile.name).name;
          this.rawFile = newFile;
        } catch (err) {
          this.$log.error(err);
        }
      }
      // reset value to null so we can get the "change" event to run
      // in any case. Even when the user selects the same file
      evt.target.value = null;
    },
    getBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob(blob => resolve(blob));
      });
    },
    async uploadFile() {
      try {
        let body = this.rawFile;
        if (this.crop) {
          const canvas = this.cropper.getCroppedCanvas({
            width: this.image.width || 512,
            height: this.image.height || 512,
            imageSmoothingQuality: 'high',
          });
          body = await this.getBlob(canvas);
        } else {
          this.openCropper = false;
        }
        // send off to our s3 upload mixin
        const file = await this.startUpload({
          file: this.fileName,
          body,
        });
        this.$emit('on-uploaded', file);
        if (this.crop) {
          this.destroyCropper();
        }
        this.$notify({
          title: NOTIFICATION_SUCCESS,
          text: 'Archivo subido',
          type: 'success',
        });
      } catch (error) {
        const message = error.message || error;
        if (!message.match(/aborted/gi)) {
          this.$log.error(message);
          this.$notify({
            title: NOTIFICATION_ERROR,
            text: message,
            type: 'error',
            duration: -1,
          });
        }
      } finally {
        this.filePicked = false;
      }
    },
    destroyCropper() {
      this.openCropper = false;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    cancelUpload() {
      this.destroyCropper();
      this.filePicked = false;
      if (this.activeUpload && this.activeUpload.s3) {
        this.activeUpload.s3.abort();
      }
    },
    replaceExt(params) {
      const pos = params.file.lastIndexOf('.');
      return params.file.substr(0, pos < 0 ? params.file.length : pos) + params.ext;
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    },
    getFile(path) {
      const name = path.split('/').pop();
      const ext = name.split('.').pop();
      return { name, ext };
    },
    async startUpload(params) {
      const {
        Bucket = process.env.VUE_APP_S3_UPLOADS_BUCKET,
        ACL = 'public-read',
        body,
        file,
      } = params;
      this.activeUpload = {
        id: Date.now(),
        file: this.getFile(file).name,
        progress: '0%',
        message: 'Uploading...',
        status: 'uploading',
        s3: null,
      };
      // check for expired sessions first
      await this.$checkSession();
      const s3 = new this.$aws.S3({ region: process.env.VUE_APP_S3_REGION });
      // build payload to send to s3
      const payload = {
        Bucket,
        Body: body,
        ACL,
        Key: `${this.bucketPath}${file}`,
        ContentType: mime.getType(file),
        ServerSideEncryption: 'AES256',
      };
      return new Promise((resolve, reject) => {
        const upload = s3.upload(payload);
        this.activeUpload.s3 = upload;
        upload.on('httpUploadProgress', (progress) => {
          // api: https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/Request.html#httpUploadProgress-event
          // update progress data for visuals (progress bars etc.)
          const percentage = parseInt((progress.loaded * 100) / progress.total, 10);
          this.activeUpload.progress = `${percentage}%`;
        });
        upload.send((error, data) => {
          if (error) {
            const message = error.message || error;
            if (message.match(/aborted/gi)) {
              this.activeUpload = null;
            } else {
              this.activeUpload.status = 'error';
              this.activeUpload.message = message;
            }
            reject(error);
            return;
          }
          this.activeUpload.status = 'success';
          this.activeUpload.message = 'Upload Complete!';
          setTimeout(() => {
            this.activeUpload = null;
          }, 500);
          resolve(data);
        });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.lcu-upload-wrapper {
  display: inline-block;
  width: 100%;
}

.lcu-upload-right {
  display: flex;
  justify-content: flex-end;

  @media screen and (min-width: $tablet-min) {
    width: 100%;
  }
}

.lcu-buttons-container {
  display: flex;
  justify-content: flex-end;
}

.lcu-button-upload {
  background-color: transparent;
  border: 2px solid $legacy-primary;
  border-radius: 3px;
  color: $legacy-primary;
  font-weight: 600;
  overflow: hidden;
  padding: 10px 15px;
  position: relative;
  text-align: center;
  text-overflow: ellipsis;
  transition-duration: .1s;
  white-space: nowrap;

  &:hover {
    background-color: $legacy-primary;
    box-shadow: 0px 2px 5px 2px rgb($legacy-primary, .18);
    color: $white;
    cursor: pointer;
    transform: translateY(-1px);
  }
}

.lcu-button-cancel {
  background-color: $gray-lightest;
  border: 2px solid transparent;
  border-radius: 3px;
  color: $black;
  margin-right: 5px;
  padding: 10px 15px;
  text-align: center;
  transition-duration: .1s;

  &:hover {
    box-shadow: 0px 2px 5px 2px rgb($black, .18);
    cursor: pointer;
    transform: translateY(-1px);
  }
}

.lcu-button-uploadText {

  &:before {
    content: '\e818';
    display: inline-block;
    font-family: 'lcu';
    margin-right: 5px;
  }
}

.lcu-button-chooseText {

  &:before {
    content: '\e812';
    display: inline-block;
    font-family: 'lcu';
    margin-right: 5px;
  }
}

.lcu-button-uploading {
  border: 2px solid $legacy-primary;
  padding: 1px 15px;
  width: 100%;

  &:hover {
    background-color: transparent;
    box-shadow: none;
    cursor: default;
    transform: translateY(0px);
  }
}

.lcu-upload-hide {
  visibility: hidden;
}

.lcu-upload {
  cursor: pointer;
  display: none;
  font-size: 14pt;
  left: 0;
  margin: 0;
  opacity: 0;
  padding: 2px;
  position: absolute;
  top: 0;
}

.lcu-upload-progress {
  align-items: center;
  background-color: $legacy-primary;
  color: $white;
  display: flex;
  height: 100%;
  justify-content: flex-end;
  left: 0;
  padding-right: 3px;
  position: absolute;
  top: 0;
  transition-duration: .1s;
  width: 0%;
}

.lcu-upload-perc {
  font-size: 12px;
}

.lcu-cropper-open {
  margin: 10px 0;
}

.lcu-upload-cropper {
  margin-bottom: 10px;
}
</style>
